import { useGetAssignment } from '@repo/api/assignment/useGetAssignment';
import { useGetAssignmentSubmissions } from '@repo/api/assignment/useGetAssignmentSubmissions';
import { CircleSlash } from 'lucide-react';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';

import AssignmentManageHeader from '@/components/features/manage/manage-assignments/AssignmentManageHeader';
import AssignmentSubmissionsTable from '@/components/features/manage/manage-assignments/AssignmentSubmissionsTable';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

export default function ManageAssignmentPage() {
  const { assignmentId: assignmentIdParam } = useParams<{
    assignmentId?: string;
  }>();
  const assignmentId = assignmentIdParam ? Number(assignmentIdParam) : undefined;
  const isValidId = assignmentId !== undefined && !Number.isNaN(assignmentId);

  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const selectedGroupId = groupParam ? Number(groupParam) : undefined;

  const assignmentQuery = useGetAssignment(isValidId ? assignmentId : undefined);
  const submissionsQuery = useGetAssignmentSubmissions(isValidId ? assignmentId : undefined);

  const assignment = assignmentQuery.data;

  const groupUserIds = useMemo(() => {
    if (selectedGroupId === undefined) return undefined;
    const group = assignment?.groups.find((g) => g.id === selectedGroupId);
    return new Set(group?.users.map((user) => user.id) ?? []);
  }, [assignment?.groups, selectedGroupId]);

  const submissions = useMemo(() => {
    const list = submissionsQuery.data ?? [];
    if (!groupUserIds) return list;
    return list.filter((submission) => groupUserIds.has(submission.userId));
  }, [submissionsQuery.data, groupUserIds]);

  const onGroupChange = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value === 'all') {
          prev.delete('group');
        } else {
          prev.set('group', value);
        }
        return prev;
      },
      { replace: true }
    );
  };

  if (!isValidId) {
    return <div className="text-muted-foreground flex h-full w-full items-center justify-center p-4">Nieprawidłowy identyfikator zadania.</div>;
  }

  if (assignmentQuery.isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{assignmentQuery.errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{assignmentQuery.errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      </div>
    );
  }

  if (assignmentQuery.isPending || submissionsQuery.isPending) {
    return (
      <div className="base-direction-col">
        <div className="bg-background flex w-full flex-col gap-2 rounded-2xl border p-4">
          <Skeleton className="h-5 w-12 rounded-sm" />
          <Skeleton className="my-2 h-4 w-24 rounded-sm" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
            <Skeleton className="h-6 w-18 rounded-sm" />
          </div>
        </div>
        <div className="bg-background flex w-full flex-col gap-2 rounded-2xl border p-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Nie znaleziono zadania</EmptyStateTitle>
            <EmptyStateDescription>To zadanie nie istnieje lub zostało usunięte.</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="base-direction-col">
      <AssignmentManageHeader assignment={assignment} submissionsCount={submissions.length} />

      <AssignmentSubmissionsTable
        assignmentId={assignmentId}
        data={submissions}
        groupSelect={
          assignment.groups.length > 0 ? (
            <Select value={selectedGroupId ? String(selectedGroupId) : 'all'} onValueChange={onGroupChange}>
              <SelectTrigger className="w-full sm:w-[12rem]">
                <SelectValue placeholder="Wszystkie grupy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie grupy</SelectItem>
                {assignment.groups.map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : undefined
        }
      />
    </div>
  );
}
